import { z } from 'zod';
import { IsoTimestampSchema, UuidSchema } from './common';
import { ConversationVerdictSchema } from './conversation';

export const AUDIT_RUN_STATUSES = ['running', 'done', 'error'] as const;
export const AuditRunStatusSchema = z.enum(AUDIT_RUN_STATUSES);
export type AuditRunStatus = z.infer<typeof AuditRunStatusSchema>;

export const AUDIT_RESULT_STATUSES = ['ok', 'error'] as const;
export const AuditResultStatusSchema = z.enum(AUDIT_RESULT_STATUSES);
export type AuditResultStatus = z.infer<typeof AuditResultStatusSchema>;

// --- Trigger (admin) ---
// Same filter shape as the conversations list: pick the slice to audit, capped by
// limit (each conversation is one judge LLM call, so the cap bounds cost).
export const AuditTriggerSchema = z.object({
  label: z.string().max(200).optional(),
  applicationId: UuidSchema.optional(),
  from: IsoTimestampSchema.optional(),
  to: IsoTimestampSchema.optional(),
  limit: z.number().int().min(1).max(200).default(25),
  model: z.string().min(1).max(128).optional(),
});
export type AuditTrigger = z.infer<typeof AuditTriggerSchema>;

export const AuditTriggerResponseSchema = z.object({
  id: UuidSchema,
  total: z.number().int().nonnegative(),
});
export type AuditTriggerResponse = z.infer<typeof AuditTriggerResponseSchema>;

// --- Read (admin panel) ---

export const AuditRunSummarySchema = z.object({
  id: UuidSchema,
  label: z.string().nullable(),
  applicationId: UuidSchema.nullable(),
  applicationName: z.string().nullable(),
  model: z.string(),
  status: AuditRunStatusSchema,
  total: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  passCount: z.number().int().nonnegative(),
  partialCount: z.number().int().nonnegative(),
  failCount: z.number().int().nonnegative(),
  naCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  avgScore: z.number().int().nullable(),
  costUsd: z.number().nonnegative(),
  error: z.string().nullable(),
  createdAt: IsoTimestampSchema,
  finishedAt: IsoTimestampSchema.nullable(),
});
export type AuditRunSummary = z.infer<typeof AuditRunSummarySchema>;

export const AuditResultSchema = z.object({
  id: UuidSchema,
  conversationId: UuidSchema,
  status: AuditResultStatusSchema,
  verdict: ConversationVerdictSchema.nullable(),
  score: z.number().int().nullable(),
  reasoning: z.string().nullable(),
  issues: z.array(z.string()),
  model: z.string().nullable(),
  costUsd: z.number().nonnegative().nullable(),
  error: z.string().nullable(),
  createdAt: IsoTimestampSchema,
  // Conversation context for display (joined).
  conversationOutcome: z.string().nullable(),
  conversationChannel: z.string().nullable(),
  conversationTurns: z.number().int().nonnegative(),
});
export type AuditResult = z.infer<typeof AuditResultSchema>;

export const AuditRunDetailSchema = AuditRunSummarySchema.extend({
  results: z.array(AuditResultSchema),
});
export type AuditRunDetail = z.infer<typeof AuditRunDetailSchema>;

export const AuditRunListResponseSchema = z.object({
  items: z.array(AuditRunSummarySchema),
});
export type AuditRunListResponse = z.infer<typeof AuditRunListResponseSchema>;
